import { useEffect, useRef } from 'react';

export interface CtxTarget {
  type: 'node' | 'edge' | 'pane';
  id: string | null;
  x: number;
  y: number;
}

export interface CtxItem {
  label: string;
  shortcut?: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}

interface CanvasContextMenuProps {
  ctx: CtxTarget | null;
  onClose: () => void;
  buildItems: (ctx: CtxTarget) => Array<CtxItem | 'separator'>;
}

/**
 * Menu de contexto flutuante (botão direito) para nó, aresta ou canvas.
 * Posicionado em coordenadas de viewport (clientX/clientY).
 */
export function CanvasContextMenu({ ctx, onClose, buildItems }: CanvasContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ctx) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [ctx, onClose]);

  if (!ctx) return null;
  const items = buildItems(ctx);

  return (
    <div
      ref={ref}
      className="fixed z-50 min-w-[190px] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
      style={{ left: ctx.x, top: ctx.y }}
    >
      {items.map((it, i) =>
        it === 'separator' ? (
          <div key={`sep-${i}`} className="my-1 h-px bg-border" />
        ) : (
          <button
            key={it.label}
            type="button"
            disabled={it.disabled}
            onClick={() => {
              it.onClick();
              onClose();
            }}
            className={`flex w-full items-center justify-between gap-4 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40 ${
              it.danger ? 'text-destructive hover:text-destructive' : ''
            }`}
          >
            <span>{it.label}</span>
            {it.shortcut && (
              <span className="text-xs tracking-widest text-muted-foreground">{it.shortcut}</span>
            )}
          </button>
        ),
      )}
    </div>
  );
}
